import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Components
import { ComponentErrorBoundary } from "components/molecules/ErrorStates";
import { useFormattedCarbonReductionData } from "pages/BuildingPage/Helper/BuildingPage.helper";
import CarbonReductionPlanCsvExporter from "pages/BuildingPage/Helper/CarbonReductionPlanCsvExporter";

// hooks
import posthog from "posthog-js";
import CsvDownloadButton from "react-json-to-csv";
import useBuildingSidebarState from "recoilStore/CustomPlan/useBuildingSidebarState";

// styling
import { useSelectedBuildingModelCarbonReductionPlan } from "hooks/Building/useBuildingModelCarbonReductionPlan";
import { ExportIcon } from "components/atoms/Icon";
import { Paragraph } from "components/atoms/Typography";
import errorFallbackUI from "components/molecules/ErrorFallbackUI.tsx/ErrorFallbackUI";
import { Table } from "components/molecules/Table";
import SearchBar from "components/organisms/SearchBar/SearchBar";
import { TABLE_IDS } from "utils/constants";
import CustomPlan from "../CustomPlan/CustomPlan";
import "./ProjectsReport.scss";

interface CustomPlanProviderProps {
    bmcrp: BuildingModelCarbonReductionPlan;
    buildingModel: Building;
}

function CustomPlanProvider({ bmcrp, buildingModel }: CustomPlanProviderProps) {
    const { state: buildingSidebarState, setState: setBuildingSidebarState } = useBuildingSidebarState();

    useEffect(() => {
        setBuildingSidebarState({
            ...buildingSidebarState,
            currentCustomPlanDetails: {
                id: bmcrp.id,
                name: bmcrp.name,
                plan: bmcrp.plan,
            },
        });
    }, [bmcrp]);

    return <CustomPlan buildingModel={buildingModel} buildingModelCarbonReductionPlan={bmcrp} />;
}

function PlanMeasures() {
    const {
        bmcrp: selectedBMCRP,
        buildingModel,
        isLoading: isLoadingBMCRP,
    } = useSelectedBuildingModelCarbonReductionPlan();

    const navigate = useNavigate();

    if (isLoadingBMCRP || !buildingModel) return null;

    if (!selectedBMCRP) return <div />;

    const crp = selectedBMCRP.plan;
    const measures: CarbonReductionMeasure[] = crp.carbonReductionMeasures;
    const { tableColumns, dataSource } = useFormattedCarbonReductionData(
        measures,
        buildingModel.grossFloorArea,
        crp.carbonReductionPlanUid
    );

    if (!dataSource || !tableColumns) return null;

    const exportData = new CarbonReductionPlanCsvExporter(crp, buildingModel.grossFloorArea).toCsvRows();

    if (selectedBMCRP.planType === "custom") {
        return <CustomPlanProvider bmcrp={selectedBMCRP} buildingModel={buildingModel} />;
    }

    const handleRowClick = (row: BuildingProjectTableRow) => {
        navigate(`/building/${buildingModel.buildingModelUid}/measures/${row.carbonReductionMeasureUid}`);
        posthog.capture("projects_table_row_clicked", {
            project: row.carbonReductionMeasureType,
        });
    };

    return (
        <div
            style={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                padding: 13,
            }}
        >
            <ComponentErrorBoundary fallback={errorFallbackUI("tableError")} originComponent="ProjectsReport">
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginLeft: "5px",
                    }}
                >
                    <SearchBar tableId={TABLE_IDS.PROJECTS_REPORTS} />
                    <CsvDownloadButton
                        data={exportData}
                        filename="buildingProjectsData.csv"
                        className="projects-report-export-button"
                        delimiter=","
                    >
                        <div className="measures-page-flex-container">
                            <ExportIcon />
                            <Paragraph>Export</Paragraph>
                        </div>
                    </CsvDownloadButton>
                </div>
                <div style={{ backgroundColor: "white" }}>
                    <Table
                        dataSource={dataSource}
                        columns={tableColumns}
                        className="projects-report-table"
                        pagination={false}
                        onRowClick={handleRowClick}
                        parentComponentWithSearchBarId={TABLE_IDS.PROJECTS_REPORTS}
                        defaultOrderBy={{ key: "yearApplied", order: "asc" }}
                        getRowHref={(row) =>
                            `/building/${buildingModel.buildingModelUid}/measures/${row.carbonReductionMeasureUid}`
                        }
                    />
                </div>
            </ComponentErrorBoundary>
        </div>
    );
}

export default PlanMeasures;
